import type { ConversationPlacement, CrowdexAssistant } from '@crowdex/agent-contracts/api';
import type { ToolRef } from '@crowdex/plugin-contracts';
import { ArrowUpRight, Bot, GitBranch, Workflow } from 'lucide-react';
import { statusLabel, statusTone } from '@/components/desktop-message-part/ref-preview';
import {
  SummaryButtonRow,
  SummaryRow,
  SummarySection,
} from '@/components/desktop-summary/summary-primitives';
import { ItemIcon } from '@/components/item-icon';
import type { RefApp } from '@/features/apps/ref-targets';
import type { ChildConversation } from '@/lib/conversations/child-conversation';
import { originLabel } from '@/lib/conversations/child-conversation';
import { type LiveTaskStatus, liveTaskLabel } from '@/lib/conversations/live-task-status';
import { type RefPlace, refActionLabel } from '@/lib/conversations/tool-refs';

/** What the Summary's ref sections need of the shell: a ref's app, its place, how it opens. */
export type SummaryRefTargets = {
  appOf: (app: string) => RefApp;
  placeOf: (ref: ToolRef) => RefPlace;
  open: (ref: ToolRef) => void;
};

/** A ref's state, in its tone ("Merged" in green, "Failed" in red). */
function Status({ status }: { status: string | undefined }) {
  if (status === undefined) return null;
  return <span className={statusTone(status)}>{statusLabel(status)}</span>;
}

/** "Read · CrowBox": what was done with it, and its app. */
function ActionAndApp({ action, app }: { action: string | null; app: string }) {
  return (
    <>
      {action !== null && <span>{action}</span>}
      {action !== null && ' · '}
      <span>{app}</span>
    </>
  );
}

/**
 * "App items": what the conversation read or wrote of its apps (CrowDrive files, CrowBox mails,
 * CrowRoom meetings), each opening docked in the side panel as its preview does.
 */
export function SummaryItemsSection({
  refs,
  targets,
}: {
  refs: readonly ToolRef[];
  targets: SummaryRefTargets;
}) {
  const items = refs.filter((ref) => targets.placeOf(ref) === 'app');
  if (items.length === 0) return null;
  return (
    <SummarySection
      count={items.length}
      rows={items.map((ref) => {
        const app = targets.appOf(ref.app);
        return (
          <SummaryButtonRow
            icon={<ItemIcon className="size-4" icon={app.icon} src={app.iconSrc} />}
            key={`${ref.app}:${ref.type ?? ''}:${ref.id}`}
            onClick={() => targets.open(ref)}
            title={[app.title, ref.detail, ref.title].filter(Boolean).join(' · ')}
            trailing={<ActionAndApp action={refActionLabel(ref.action)} app={app.title} />}
          >
            {ref.title}
          </SummaryButtonRow>
        );
      })}
      title="App items"
    />
  );
}

/**
 * "Services": the results of the services it used (pull requests, issues, merge requests, Slack
 * threads), with their state; each opens at its service ↗. The branch's own pull request stays
 * in Code.
 */
export function SummaryServicesSection({
  refs,
  targets,
  branchPullRequestUrl,
}: {
  refs: readonly ToolRef[];
  targets: SummaryRefTargets;
  branchPullRequestUrl?: string | null | undefined;
}) {
  const results = refs.filter(
    (ref) =>
      targets.placeOf(ref) === 'service' &&
      (branchPullRequestUrl == null || ref.url !== branchPullRequestUrl),
  );
  if (results.length === 0) return null;
  return (
    <SummarySection
      count={results.length}
      rows={results.map((ref) => {
        const app = targets.appOf(ref.app);
        return (
          <SummaryButtonRow
            icon={<ItemIcon className="size-4" icon={app.icon} src={app.iconSrc} />}
            key={`${ref.app}:${ref.type ?? ''}:${ref.id}`}
            onClick={ref.url === undefined ? undefined : () => targets.open(ref)}
            title={[app.title, ref.detail, ref.title].filter(Boolean).join(' · ')}
            trailing={
              <span className="inline-flex items-center gap-1">
                {ref.status === undefined ? (
                  <span>{app.title}</span>
                ) : (
                  <Status status={ref.status} />
                )}
                {ref.url !== undefined && <ArrowUpRight aria-hidden className="size-3.5" />}
              </span>
            }
          >
            {ref.title}
          </SummaryButtonRow>
        );
      })}
      title="Services"
    />
  );
}

/** A task of the conversation: from a tool's ref, or one of its child conversations. */
export type SummaryTask = {
  id: string;
  title: string;
  /** A workflow's task (its run), or a conversation it handed work to. */
  workflow: boolean;
  status?: string | undefined;
  /** How it stands now (the sidebar's sources), over the state its ref had when it started. */
  live?: LiveTaskStatus | null | undefined;
  ref?: ToolRef | undefined;
};

/**
 * Its tasks: what its tools started (`start_conversation`'s refs, a workflow's runs) and its child
 * conversations, each once, the refs' first.
 */
export function summaryTasks(
  refs: readonly ToolRef[],
  children: readonly { id: string; title: string; workflow: boolean }[],
  /** How a task stands now, when the shell knows it (`liveTaskStatus`). */
  live: (id: string) => LiveTaskStatus | null = () => null,
): SummaryTask[] {
  const tasks = new Map<string, SummaryTask>();
  for (const ref of refs) {
    if (ref.kind !== 'work') continue;
    tasks.set(ref.id, {
      id: ref.id,
      title: ref.title,
      workflow: ref.type === 'task' || ref.type === 'run',
      status: ref.status,
      ref,
    });
  }
  for (const child of children)
    if (!tasks.has(child.id)) tasks.set(child.id, { ...child, status: undefined });
  return [...tasks.values()].map((task) => {
    const now = live(task.id);
    return now === null ? task : { ...task, live: now };
  });
}

/**
 * "Tasks and runs": what it launched, with how each stands now (live, its round in a loop); one opens its conversation (read only
 * for a workflow's run, as its own page says).
 */
export function SummaryTasksSection({
  tasks,
  onOpen,
}: {
  tasks: readonly SummaryTask[];
  onOpen: (task: SummaryTask) => void;
}) {
  if (tasks.length === 0) return null;
  return (
    <SummarySection
      count={tasks.length}
      rows={tasks.map((task) => (
        <SummaryButtonRow
          icon={task.workflow ? <Workflow /> : <GitBranch />}
          key={task.id}
          onClick={() => onOpen(task)}
          title={[task.ref?.detail, task.title].filter(Boolean).join(' · ')}
          trailing={
            task.live != null ? (
              <span className={statusTone(task.live.status)} data-task-status={task.live.status}>
                {liveTaskLabel(task.live)}
              </span>
            ) : (
              <Status status={task.status} />
            )
          }
        >
          {task.title}
        </SummaryButtonRow>
      ))}
      title="Tasks and runs"
    />
  );
}

/** The assistant a conversation belongs to: its placement's, else none. */
export function conversationAssistant(
  conversationId: string,
  placements: Readonly<Record<string, ConversationPlacement>>,
  projects: readonly CrowdexAssistant[],
): CrowdexAssistant | null {
  const own = projects.find((project) => project.conversationId === conversationId);
  if (own !== undefined) return own;
  const projectId = placements[conversationId]?.projectId ?? null;
  return projectId === null
    ? null
    : (projects.find((project) => project.id === projectId && project.archivedAt === null) ?? null);
}

/**
 * "Assistant", at the top: the conversation's assistant (its channel opens from it) and what
 * started it (another conversation, a workflow, or the user). Neither: no section.
 */
export function SummaryAssistantSection({
  assistant,
  child,
  isChannel,
  onOpenAssistant,
  onOpenParent,
}: {
  assistant: Pick<CrowdexAssistant, 'id' | 'name' | 'color'> | null;
  child: ChildConversation | null;
  /** The conversation is the assistant's channel itself. */
  isChannel: boolean;
  onOpenAssistant: () => void;
  onOpenParent: (id: string) => void;
}) {
  if (assistant === null && child === null) return null;
  return (
    <SummarySection title="Assistant">
      {assistant !== null && (
        <SummaryButtonRow
          icon={
            <span
              aria-hidden
              className="size-2.5 rounded-[3px]"
              style={{ backgroundColor: assistant.color }}
            />
          }
          onClick={isChannel ? undefined : onOpenAssistant}
          title={isChannel ? `${assistant.name}’s channel` : `Open ${assistant.name}’s channel`}
          trailing={isChannel ? 'Channel' : undefined}
        >
          {assistant.name}
        </SummaryButtonRow>
      )}
      {child !== null ? (
        <SummaryButtonRow
          icon={<Bot />}
          onClick={() => onOpenParent(child.parentId)}
          title={originLabel(child)}
        >
          {originLabel(child)}
        </SummaryButtonRow>
      ) : (
        !isChannel && <SummaryRow label="Started by">You</SummaryRow>
      )}
    </SummarySection>
  );
}
